import { QRCode } from 'antd';
import Image from 'next/image';
import { useEffect, useRef, useState } from 'react';

import Loading from '@/components/common/Loading';
import Tab, { TabItem } from '@/components/common/Tab';
import { MembershipLink } from '@/components/UIComponents/UserAccount/const';
import { UseTranslateType } from '@/hooks/useTranslate';
import { fc_purchase_alert_expose } from '@/utils/eventlog/eventNewConfig';

import CommonModal from '../../..';
import { Callback, channelPay } from '../hook/channelPay';
import CloseIcon from '../icon/close';
interface PayQRCodeContent {
  feature: fc_purchase_alert_expose['from'];
  commodityCode: string;
  couponCode: string;
  callback: Callback;
  price: string;
  currency: string;
  onClose?: () => void;
  onTabChange: (orderId: string) => void;
}
interface PayQRCodeProps extends PayQRCodeContent {
  onOk?: () => void;
  destroyHandler: React.MutableRefObject<any>;
}

type TabActiveKey = 'wechatPay' | 'aliPay';

export interface TabContentProps {
  activeKey: TabActiveKey;
  onChange: (value: TabActiveKey) => void;
  t: UseTranslateType;
}
const TabContent = ({ t, activeKey, onChange }: TabContentProps) => {
  let items: TabItem<TabActiveKey>[] = [
    {
      key: 'wechatPay',
      label: () => {
        return (
          <div className="flex items-center gap-[6px]">
            <Image
              src="/images/wechatPay.png"
              alt="wechat"
              width={16}
              height={14}
              unoptimized
            />
            <span>{t('WeChat Pay')}</span>
          </div>
        );
      },
    },
    {
      key: 'aliPay',
      label: () => {
        return (
          <div className="flex items-center gap-[6px]">
            <Image
              src="/images/aliPay.png"
              alt="wechat"
              width={16}
              height={16}
              unoptimized
            />
            <span>{t('Ali Pay')}</span>
          </div>
        );
      },
    },
  ];

  return (
    <div className="px-[28px] pb-[12px] flex justify-center">
      <Tab
        className="bg-payment-modal-tab-bg rounded-[6px] p-[4px] shrink-0 gap-[4px]"
        itemClassName="w-[162px] h-[38px] text-[18px] font-[400]"
        sliderClassName="bg-[#35363C] rounded-[6px]"
        type="slider"
        items={items}
        activeKey={activeKey}
        onChange={onChange}
      />
    </div>
  );
};

const PayQRCode = ({
  feature,
  commodityCode,
  couponCode,
  price,
  currency,
  callback,
  onClose,
  onTabChange,
  t,
}: PayQRCodeContent & { t: UseTranslateType }) => {
  console.log(commodityCode);
  const [activeTab, setActiveTab] = useState<TabActiveKey>('wechatPay');
  const payUrl = useRef({
    aliPay: '',
    wechatPay: '',
  });
  // 防止重复轮训，tab切换后，保留一下对应的orderId
  const payOrderId = useRef({
    aliPay: '',
    wechatPay: '',
  });
  const [qrcodeLoading, setQRCodeLoading] = useState(false);
  const handleTabChange = (value: TabActiveKey) => {
    setActiveTab(value);
    onTabChange(payOrderId.current[value]);
  };
  const payHandle = async () => {
    try {
      setQRCodeLoading(true);
      const url = await channelPay({
        channel: {
          type: 'qrcode',
          data: activeTab === 'aliPay' ? 'alipay_qr' : 'wx_qr',
        },
        feature,
        commodityCode,
        couponCode,
        callback: {
          success(orderId, data) {
            payOrderId.current[activeTab] = orderId;
            callback.success(orderId, data);
          },
          error: (error) => {
            callback.error(error);
          },
        },
      });
      payUrl.current[activeTab] = url;
    } catch (error) {
      console.error('channelPay Error', error);
    } finally {
      setQRCodeLoading(false);
    }
  };
  useEffect(() => {
    if (!payUrl.current[activeTab]) {
      payHandle();
    }
  }, [activeTab]);
  return (
    <div className="relative flex flex-col items-center bg-[#232429]">
      <div className="absolute right-[18px] top-[18px] cursor-pointer" onClick={onClose}>
        <CloseIcon />
      </div>
      <div className="text-common-text-base-color text-[20px] font-[700] leading-[28px] pt-[14px]">
        {t('Scan to pay')}
      </div>
      <div className="pt-[30px] flex flex-col items-center gap-[6px]">
        <span className="text-[#F6F6F699] text-[14px] font-[500] leading-[20px]">
          {t('Payment amount')}
        </span>
        <span className="text-[#fff] text-[30px] font-[600] leading-[40px]">
          {currency}
          {price}
        </span>
      </div>
      <div className="pt-[16px]">
        <TabContent
          t={t}
          activeKey={activeTab}
          onChange={(value) => {
            handleTabChange(value);
          }}
        />
      </div>
      <div className="pt-[16px]">
        <div className=" bg-[#35363C] w-[208px] h-[208px] rounded-[4px] p-[10px] relative box-content">
          {qrcodeLoading ? (
            <Loading size={40} />
          ) : (
            <QRCode
              value={payUrl.current[activeTab] || ''}
              size={208}
              color="#000"
              bgColor="#fff"
              style={{ borderRadius: 0 }}
            />
          )}
        </div>
      </div>
      <div className="py-[16px]  text-[12px] font-[400] ">
        <span className="text-[#F6F6F659]">{t('Payment is deemed as consent')}</span>
        <span
          className="text-[#8a94fb] cursor-pointer"
          onClick={() => {
            window.open(MembershipLink, '_blank');
          }}
        >
          《{t('Payment Service Agreement')}》
        </span>
      </div>
    </div>
  );
};

export async function PayQRCodeShow(options: PayQRCodeProps) {
  CommonModal.show(
    (t) => ({
      width: 438,
      footer: false,
      customPadding: {
        x: 0,
        y: 0,
      },
      modalClassName: 'pay-qrcode-modal',
      content: (close) => (
        <PayQRCode
          {...options}
          t={t}
          onClose={() => {
            close();
            options.onClose?.();
          }}
        />
      ),
    }),
    options.destroyHandler,
  );
}
